import { useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, Sparkles, X } from 'lucide-react';
import api from '@/lib/api';
import { MATERIALS } from '@/lib/planMaterials';

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

function ResultList({ title, rows, tid }) {
  if (!rows.length) return null;
  return (
    <div data-testid={tid}>
      <p className="tm-label">{title} ({rows.length})</p>
      <ul className="text-sm divide-y divide-slate-100 max-h-60 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.name} className="flex items-center justify-between gap-3 py-1.5">
            <span className="min-w-0 truncate"><span className="font-semibold">{r.name}</span>{r.source !== r.name && <span className="text-slate-400"> ← {r.source}</span>}</span>
            <span className="shrink-0">{r.old != null && <span className="text-slate-400 line-through mr-2">{gbp(r.old)}</span>}<span className="font-semibold">{gbp(r.price)}</span>{r.unit && <span className="text-xs text-slate-500"> / {r.unit}</span>}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Paste or upload a supplier price list (CSV, Excel, PDF or photo) — AI reads it and updates prices straight away
export default function PriceListImport({ refresh }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!text.trim() && !file) { toast.error('Paste your price list or choose a file first'); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append('text', text);
    fd.append('materials', JSON.stringify(MATERIALS.map((m) => m.name)));
    if (file) fd.append('file', file);
    try {
      const { data } = await api.post('/me/price-list/import', fd, { timeout: 180000 });
      setResult(data); setText(''); setFile(null); await refresh();
      const n = data.updated.length + data.added.length;
      if (n) toast.success(`${n} price${n === 1 ? '' : 's'} imported`); else toast.error('No prices found in that list');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not import that price list'); } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-dashed border-slate-300 rounded-xl p-5 space-y-3" data-testid="price-import">
      <div>
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Sparkles size={17} className="text-brand" /> Import a supplier price list</h3>
        <p className="text-sm text-slate-500 mt-0.5">Paste rows, or upload a CSV, Excel, PDF or photo. Matching materials are updated and anything new is added to your list automatically.</p>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="tm-input font-mono text-xs" data-testid="price-import-text"
        placeholder={'Indian sandstone paving, £42.50 per m²\nComposite decking, £85 per m²\nMOT Type 1 bulk bag, £48'} />
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:border-slate-900 cursor-pointer" data-testid="price-import-file-btn">
          <Upload size={13} /> {file ? 'Change file' : 'Choose file'}
          <input type="file" accept=".csv,.txt,.xlsx,.pdf,image/*" className="hidden" data-testid="price-import-file"
            onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }} />
        </label>
        {file && <span className="text-xs text-slate-600 inline-flex items-center gap-1" data-testid="price-import-file-name">{file.name}<button type="button" onClick={() => setFile(null)} aria-label="Remove file" data-testid="price-import-file-remove"><X size={13} /></button></span>}
        <button type="button" onClick={run} disabled={busy} className="tm-btn ml-auto" data-testid="price-import-submit">
          {busy ? <><Loader2 size={15} className="animate-spin" /> Reading prices…</> : 'Import prices'}
        </button>
      </div>
      {result && (
        <div className="space-y-3 pt-2" data-testid="price-import-result">
          <ResultList title="Updated" rows={result.updated} tid="price-import-updated" />
          <ResultList title="Added to your list" rows={result.added} tid="price-import-added" />
        </div>
      )}
    </div>
  );
}
